import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { mediaUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSettings } from "@/context/SettingsContext";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, lineKey } = useCart();
  const { settings } = useSettings();
  const threshold = settings.free_shipping_threshold || 150;
  const remaining = Math.max(0, threshold - subtotal);
  const pct = Math.min(100, (subtotal / threshold) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#F7F5F1] w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Winkelmand</SheetTitle>
        <div className="p-6 border-b border-[#E8E1D8] flex items-center justify-between">
          <p className="heading-x text-sm">WINKELMAND ({items.length})</p>
          <button onClick={() => setOpen(false)} aria-label="Sluit winkelmand" data-testid="cart-close"><X size={20} /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
            <p className="label-x">JE WINKELMAND IS LEEG</p>
            <Link to="/shop" onClick={() => setOpen(false)} className="btn-x-outline text-[0.65rem]">SHOP DE PROTOCOL</Link>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4">
              <div className="h-1 bg-[#E8E1D8] w-full">
                <div className="h-full bg-[#1D1D1B] transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="label-x mt-2">{remaining > 0 ? `NOG €${remaining.toFixed(2)} TOT GRATIS VERZENDING` : "GRATIS VERZENDING BEHAALD"}</p>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4 space-y-5">
              {items.map((it) => {
                const key = lineKey(it);
                return (
                  <div key={key} className="flex gap-4" data-testid={`cart-line-${it.product_id}`}>
                    <div className="w-20 h-28 bg-[#E8E1D8] shrink-0 overflow-hidden">
                      <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between gap-2">
                        <p className="text-xs uppercase tracking-wide font-medium">{it.name}</p>
                        <button onClick={() => removeItem(key)} aria-label="Remove" data-testid={`cart-remove-${it.product_id}`}><X size={15} /></button>
                      </div>
                      <p className="label-x mt-1">{it.size ? `MAAT ${it.size}` : ""} {it.color ? `· ${it.color}` : ""}</p>
                      <div className="flex justify-between items-center mt-3">
                        <div className="flex items-center border border-[#E8E1D8]">
                          <button className="p-1.5" onClick={() => updateQty(key, it.quantity - 1)} data-testid={`cart-dec-${it.product_id}`}><Minus size={13} /></button>
                          <span className="px-3 text-xs font-mono">{it.quantity}</span>
                          <button className="p-1.5" onClick={() => updateQty(key, it.quantity + 1)} data-testid={`cart-inc-${it.product_id}`}><Plus size={13} /></button>
                        </div>
                        <p className="text-sm font-mono">€{(it.price * it.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[#E8E1D8] p-6 space-y-4">
              <div className="flex justify-between">
                <span className="label-x">SUBTOTAAL</span>
                <span className="text-sm font-mono" data-testid="cart-subtotal">€{subtotal.toFixed(2)}</span>
              </div>
              <Link to="/checkout" onClick={() => setOpen(false)} className="btn-x w-full" data-testid="cart-checkout-btn">AFREKENEN</Link>
              <button onClick={() => setOpen(false)} className="w-full label-x py-2" data-testid="cart-continue">VERDER WINKELEN</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
